import { useEffect, useRef, useState } from 'preact/hooks'
import type { Message, ChatAttachment } from '../types'
import { FullscreenImageViewer } from './FullscreenImageViewer'
import { FullscreenDocumentViewer } from './FullscreenDocumentViewer'
import { ReportContentDialog, useReportFlagsVisible } from './ReportContentDialog'

/** Strip [user time: ...] timestamps injected by the backend */
const USER_TIME_RE = /\[user time:[^\]]*\]\s*/g

/** The closest user message before index `i` (or the last one when `i` is
 *  omitted), without its timestamp prefix — the request a report refers to. */
export function precedingUserRequest(messages: Message[], i = messages.length): string | null {
  for (let j = i - 1; j >= 0; j--) {
    if (messages[j].role === 'user') return displayContent(messages[j]) || null
  }
  return null
}

function displayContent(message: Message): string {
  let text = message.content || ''
  if (message.role === 'user') {
    text = text.replace(USER_TIME_RE, '').trim()
  }
  return text
}

interface MessageBubbleProps {
  message: Message
  onPlayTts?: (text: string) => void
  isAudioPlaying?: boolean
  onEditMessage?: (messageId: string, currentContent: string) => void
  onDeleteMessage?: (messageId: string) => void
  /** The user turn this assistant message answers; included in reports. */
  userRequest?: string | null
}

export function MessageBubble({
  message,
  onPlayTts,
  isAudioPlaying,
  onEditMessage,
  onDeleteMessage,
  userRequest,
}: MessageBubbleProps) {
  const content = displayContent(message)
  const [menuOpen, setMenuOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [reportSent, setReportSent] = useState(false)
  const reportFlagsVisible = useReportFlagsVisible()
  const menuWrapRef = useRef<HTMLDivElement | null>(null)

  // Close menu on outside click. Mirrors the sidebar session menu pattern.
  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e: MouseEvent) => {
      if (!menuWrapRef.current?.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('click', onDoc)
    return () => document.removeEventListener('click', onDoc)
  }, [menuOpen])

  if (message.role === 'system') {
    return (
      <div class="message message--system">
        <div class="message__bubble message__bubble--system">
          {content}
        </div>
      </div>
    )
  }

  const isAssistant = message.role === 'assistant'
  const isUser = message.role === 'user'
  const showMenu =
    !message.isStreaming &&
    !!content.trim() &&
    (isAssistant || isUser)

  const bubbleClass = [
    'message__bubble',
    message.isStreaming ? 'message__bubble--streaming' : '',
    showMenu ? 'message__bubble--has-menu' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const handleCopy = async () => {
    setMenuOpen(false)
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (e) {
      console.warn('Copy to clipboard failed:', e)
    }
  }

  const handlePlay = () => {
    setMenuOpen(false)
    if (onPlayTts) onPlayTts(message.content)
  }

  const handleEdit = () => {
    setMenuOpen(false)
    if (onEditMessage) onEditMessage(message.id, content)
  }

  const handleDelete = () => {
    setMenuOpen(false)
    if (onDeleteMessage) onDeleteMessage(message.id)
  }

  const handleReport = () => {
    setMenuOpen(false)
    setReportOpen(true)
  }

  const attachments = isUser && Array.isArray(message.attachments) ? message.attachments : []

  return (
    <div class={`message message--${message.role}`}>
      <div class={bubbleClass}>
        {attachments.length > 0 && (
          <MessageAttachments attachments={attachments} />
        )}
        {content || (message.isStreaming ? '' : (attachments.length > 0 ? '' : '...'))}
        {showMenu && (
          <div class="message__menu-wrap" ref={menuWrapRef}>
            {isAssistant && reportFlagsVisible && (
              // Report offensive AI output in-app (Google Play AI content policy)
              <button
                class="message__menu-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  handleReport()
                }}
                title={reportSent ? 'Reported — thank you' : 'Report inappropriate content'}
                style={{ fontSize: 13 }}
              >
                {reportSent ? '✓' : '\u{1F6A9}'}
              </button>
            )}
            <button
              class="message__menu-btn"
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen((v) => !v)
              }}
              title="Actions"
            >
              &#x22EE;
            </button>
            {menuOpen && (
              <div class={`message__menu-dropdown message__menu-dropdown--${message.role}`}>
                {isAssistant && onPlayTts && (
                  <button
                    class="message__menu-item"
                    onClick={handlePlay}
                    disabled={isAudioPlaying}
                  >
                    <span class="message__menu-icon">{'\u{25B6}\u{FE0F}'}</span>
                    {isAudioPlaying ? 'Playing...' : 'Play message'}
                  </button>
                )}
                <button class="message__menu-item" onClick={handleCopy}>
                  <span class="message__menu-icon">{'\u{1F4CB}'}</span>
                  {copied ? 'Copied!' : 'Copy to clipboard'}
                </button>
                {isUser && onEditMessage && (
                  <button class="message__menu-item" onClick={handleEdit}>
                    <span class="message__menu-icon">{'\u{270F}\u{FE0F}'}</span>
                    Edit
                  </button>
                )}
                {isUser && onDeleteMessage && (
                  <button
                    class="message__menu-item message__menu-item--danger"
                    onClick={handleDelete}
                  >
                    <span class="message__menu-icon">{'\u{1F5D1}\u{FE0F}'}</span>
                    Delete
                  </button>
                )}
                {isAssistant && (
                  <button
                    class="message__menu-item message__menu-item--danger"
                    onClick={handleReport}
                  >
                    <span class="message__menu-icon">{'\u{1F6A9}'}</span>
                    Report content
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {reportOpen && (
        <ReportContentDialog
          kind="text"
          aiContent={content}
          userRequest={userRequest}
          messageId={message.id}
          onClose={(sent) => {
            setReportOpen(false)
            if (sent) setReportSent(true)
          }}
        />
      )}
    </div>
  )
}

function MessageAttachments({ attachments }: { attachments: ChatAttachment[] }) {
  const [imageViewer, setImageViewer] = useState<string | null>(null)
  const [docViewer, setDocViewer] = useState<{ url: string; mime: string; name: string } | null>(null)

  const openAttachment = (att: ChatAttachment) => {
    // For images we already have a previewUrl (a fresh upload) or, for cloud
    // pickups, only the base64. Materialize a Blob URL from the bytes either
    // way so the fullscreen viewer can render without the data: URL hitting
    // the browser's image-src limits.
    const url = att.previewUrl || base64ToObjectUrl(att.data, att.mimeType)
    if (att.mimeType.startsWith('image/')) {
      setImageViewer(url)
    } else {
      setDocViewer({ url, mime: att.mimeType, name: att.fileName })
    }
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
      {attachments.map((att, i) => {
        const isImage = att.mimeType.startsWith('image/')
        const isPdf = att.mimeType === 'application/pdf'
        const icon = isImage ? '\u{1F5BC}\u{FE0F}' : isPdf ? '\u{1F4C4}' : '\u{1F4DD}'
        const url = att.previewUrl // only present for fresh uploads
        return (
          <button
            key={i}
            onClick={() => openAttachment(att)}
            title="Open"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.25)',
              background: 'rgba(0,0,0,0.18)',
              color: 'inherit',
              fontSize: 12,
              cursor: 'pointer',
              maxWidth: 220,
            }}
          >
            {isImage && url ? (
              <img src={url} alt="" style={{ width: 22, height: 22, borderRadius: 4, objectFit: 'cover' }} />
            ) : (
              <span style={{ fontSize: 14 }}>{icon}</span>
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {att.fileName}
            </span>
          </button>
        )
      })}
      {imageViewer && (
        <FullscreenImageViewer src={imageViewer} onClose={() => setImageViewer(null)} />
      )}
      {docViewer && (
        <FullscreenDocumentViewer
          src={docViewer.url}
          mimeType={docViewer.mime}
          name={docViewer.name}
          onClose={() => setDocViewer(null)}
        />
      )}
    </div>
  )
}

/** Decode a base64 string into a Blob: URL we can pass to <img>/<iframe>. */
function base64ToObjectUrl(b64: string, mimeType: string): string {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: mimeType || 'application/octet-stream' })
  return URL.createObjectURL(blob)
}
